import type { LayoutNode } from './types';

/**
 * What role each layout node type plays: does it answer a question, arrange
 * other nodes, or neither.
 *
 * This is a `Record` over the node-type union rather than a set of literals so
 * that adding a node to `types.ts` fails the build here until it is
 * classified. A `Set<LayoutNode['type']>` only checks that each member is a
 * valid type, never that the list is complete, so a partial list compiles
 * silently, which is how these lists drift apart. Every other classifier in
 * this directory closes the same hole with an exhaustive `switch` and
 * `assertLayoutNodeExhaustive`.
 *
 * Callers that need a narrower slice should subtract from these sets rather
 * than re-listing the types, so there stays one place a new node type is
 * classified.
 */
export type LayoutNodeKind = 'field' | 'container' | 'other';

const NODE_KIND: Record<LayoutNode['type'], LayoutNodeKind> = {
  // Fields — the node's `qid` renders an answer control.
  OASISRadio: 'field',
  OASISYesNoCheckbox: 'field',
  OASISSelect: 'field',
  OASISText: 'field',
  OASISPhysician: 'field',
  OASISDate: 'field',
  OASISTime: 'field',
  OASISNumber: 'field',
  OASISMultiselect: 'field',
  'radio-option': 'field',

  // Containers — they arrange other nodes.
  card: 'container',
  grid: 'container',
  flex: 'container',
  stack: 'container',
  box: 'container',
  indent: 'container',
  table: 'container',
  'field-row': 'container',

  // Everything else: static content, derived values, and the wrappers whose
  // meaning is a condition rather than an arrangement.
  divider: 'other',
  static: 'other',
  text: 'other',
  display: 'other',
  // Reads an answer from another document; renders no control of its own.
  'external-answer': 'other',
  'section-label': 'other',
  sum: 'other',
  conditional: 'other',
  'disabled-when': 'other',
  repeat: 'other',
  // Shares its parent `OASISMultiselect`'s qid, so it is not a field of its own.
  OASISMultiselectOption: 'other',
};

/** Every node type the layout DSL knows, derived from the classification. */
export const KNOWN_LAYOUT_NODE_TYPES: ReadonlySet<string> = new Set(
  Object.keys(NODE_KIND)
);

export function layoutNodeKind(type: LayoutNode['type']): LayoutNodeKind {
  return NODE_KIND[type];
}

/** Node types whose `qid` renders an answer control. */
export function isFieldNodeType(type: LayoutNode['type']): boolean {
  return NODE_KIND[type] === 'field';
}

/** Node types that arrange other nodes. */
export function isContainerNodeType(type: LayoutNode['type']): boolean {
  return NODE_KIND[type] === 'container';
}
